import { useMemo, useState } from "react";
import { fa, fdateParts } from "./data";
import { DatePickerField } from "./DatePickerField";

const EMPTY_FILTERS = { groupId: "all", personQuery: "", dateFrom: "", dateTo: "", showNegative: true, showPositive: true };

function dayOf(value) {
  return String(value).slice(0, 10);
}

function BuffDetailModal({ record, person, group, feedbacks, onClose }) {
  if (!record) return null;
  const isPositive = record.type === "positive";
  const applied = fdateParts(record.applied_at);

  return (
    <div className="history-modal-bg" style={{ display: "block" }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <section className="history-modal" role="dialog" aria-modal="true">
        <div className="history-modal-head">
          <div>
            <h3>گزارش باف</h3>
            <div className="who">کارشناس: {person?.name || "—"} {group ? "· گروه: " + group.name : ""}</div>
          </div>
          <button className="modal-close" type="button" aria-label="بستن" onClick={onClose}>×</button>
        </div>
        <div className="history-summary">
          <span className={"feedback-type-badge" + (isPositive ? " positive" : " negative")}>{isPositive ? "باف مثبت" : "باف منفی"}</span>
          {" "}· {fa(record.count)} چوب خط · اعمال‌شده در {applied.date} ساعت {applied.time}
        </div>
        {record.note && (
          <div className="history-item history-item-text" style={{ marginBottom: 12 }}>
            <b>توضیحات اعمال:</b> {record.note}
          </div>
        )}
        <div className="history-list">
          {feedbacks.map((f, idx) => {
            const dp = fdateParts(f.created_at);
            return (
              <article className="history-item" key={f.id}>
                <div className="history-item-head">
                  <div className="history-item-number">چوب خط {fa(idx + 1)}</div>
                  <div className="history-item-date">{dp.date}</div>
                </div>
                <div className="history-item-meta">
                  <div className="history-time">ساعت {dp.time || "—"}</div>
                </div>
                {f.caption && <div className="history-item-text">{f.caption}</div>}
                {f.image && <img className="history-item-image" src={f.image} alt="تصویر بازخورد" />}
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}

export default function BuffHistoryPage({ state, currentUser, onBack }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [openRecordId, setOpenRecordId] = useState(null);

  const feedbackById = useMemo(() => {
    const map = {};
    state.feedbacks.forEach(f => { map[f.id] = f; });
    return map;
  }, [state.feedbacks]);

  const personById = (id) => state.people.find(p => String(p.id) === String(id)) || null;
  const groupOfPerson = (person) => person ? state.groups.find(g => String(g.id) === String(person.group_id)) || null : null;

  const records = useMemo(() => {
    return (state.appliedBuffs || [])
      .filter(r => {
        const person = personById(r.people_id);
        const group = groupOfPerson(person);
        if (filters.groupId !== "all" && String(group?.id) !== String(filters.groupId)) return false;
        if (filters.personQuery.trim() && !(person?.name || "").includes(filters.personQuery.trim())) return false;
        const day = dayOf(r.applied_at);
        if (filters.dateFrom && day < filters.dateFrom) return false;
        if (filters.dateTo && day > filters.dateTo) return false;
        if (r.type === "negative" && !filters.showNegative) return false;
        if (r.type === "positive" && !filters.showPositive) return false;
        return true;
      })
      .sort((a, b) => String(b.applied_at).localeCompare(String(a.applied_at)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, filters]);

  const openRecord = (state.appliedBuffs || []).find(r => r.id === openRecordId) || null;
  const openPerson = openRecord ? personById(openRecord.people_id) : null;
  const openGroup = openRecord ? groupOfPerson(openPerson) : null;
  const openFeedbacks = openRecord
    ? (Array.isArray(openRecord.feedbacks) && openRecord.feedbacks.length
        ? openRecord.feedbacks
        : (openRecord.feedback_ids || []).map(id => feedbackById[id]).filter(Boolean))
    : [];

  const active = filters.groupId !== "all" || filters.personQuery || filters.dateFrom || filters.dateTo || !filters.showNegative || !filters.showPositive;

  return (
    <main className="app-shell">
      <section className="hero">
        <div className="hero-line">
          <div>
            <h1>باف‌های اعمال‌شده</h1>
            <p>فهرست بافی که تایید و اعمال شده‌اند؛ روی هر ردیف کلیک کنید تا گزارش کامل آن را ببینید.</p>
          </div>
        </div>
      </section>

      <section className="toolbar filters-bar" aria-label="فیلترها">
        <div className="filters-grid">
          <div className="filter-field">
            <label htmlFor="histFilterGroup">فیلتر گروه</label>
            <select id="histFilterGroup" className="input select" value={filters.groupId} onChange={(e) => setFilters(f => ({ ...f, groupId: e.target.value }))}>
              <option value="all">همه گروه‌ها</option>
              {state.groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <div className="filter-field">
            <label htmlFor="histFilterPerson">فیلتر شخص</label>
            <input id="histFilterPerson" className="input" placeholder="نام کارشناس…" value={filters.personQuery} onChange={(e) => setFilters(f => ({ ...f, personQuery: e.target.value }))} />
          </div>
          <DatePickerField id="histFilterFrom" label="از تاریخ" valueISO={filters.dateFrom} onChange={(v) => setFilters(f => ({ ...f, dateFrom: v }))} />
          <DatePickerField id="histFilterTo" label="تا تاریخ" valueISO={filters.dateTo} onChange={(v) => setFilters(f => ({ ...f, dateTo: v }))} />
        </div>
        <div className="switch-row">
          <label className="switch">
            <input type="checkbox" checked={filters.showNegative} onChange={(e) => setFilters(f => ({ ...f, showNegative: e.target.checked }))} />
            <span className="switch-track"><span className="switch-thumb"></span></span>
          </label>
          <span>نمایش باف منفی</span>
        </div>
        <div className="switch-row">
          <label className="switch">
            <input type="checkbox" checked={filters.showPositive} onChange={(e) => setFilters(f => ({ ...f, showPositive: e.target.checked }))} />
            <span className="switch-track"><span className="switch-thumb"></span></span>
          </label>
          <span>نمایش باف مثبت</span>
        </div>
        <div className="filters-actions">
          <button className="btn" type="button" disabled={!active} onClick={() => setFilters(EMPTY_FILTERS)}>حذف فیلترها</button>
        </div>
      </section>

      <section className="groups">
        {!records.length && <div className="empty">تا کنون هیچ بافی اعمال نشده است.</div>}
        {records.map(r => {
          const person = personById(r.people_id);
          const group = groupOfPerson(person);
          const isPositive = r.type === "positive";
          const applied = fdateParts(r.applied_at);
          return (
            <article className="person in-group" key={r.id} style={{ cursor: "pointer" }} onClick={() => setOpenRecordId(r.id)}>
              <div className="person-top">
                <div className="name-block">
                  <div className="name">{person?.name || "کارشناس حذف‌شده"}</div>
                  <div className="meta">{group ? group.name + " · " : ""}اعمال‌شده در {applied.date} ساعت {applied.time}</div>
                </div>
                <span className={"feedback-type-badge" + (isPositive ? " positive" : " negative")}>{isPositive ? "باف مثبت" : "باف منفی"}</span>
              </div>
              <div className="history-summary" style={{ marginTop: 8 }}>
                {fa(r.count)} چوب خط{r.note ? " · " + r.note : ""}
              </div>
            </article>
          );
        })}
      </section>

      <BuffDetailModal
        record={openRecord}
        person={openPerson}
        group={openGroup}
        feedbacks={openFeedbacks}
        onClose={() => setOpenRecordId(null)}
      />
    </main>
  );
}
